import React from 'react';
import { ArrowRight } from 'lucide-react';
import BackgroundAnimation from '../../components/BackgroundAnimation';

const LeadGeneration = () => {
  const strategies = [
    { title: 'Precision Targeting', desc: 'Advanced audience segmentation', items: ['Demographic analysis', 'Interest-based targeting', 'Lookalike audiences', 'Retargeting campaigns'] },
    { title: 'ROI Optimization', desc: 'Maximize return on investment', items: ['Cost per lead optimization', 'Budget distribution', 'Bid management', 'Performance tracking'] },
    { title: 'Lead Nurturing', desc: 'Convert prospects into customers', items: ['Email sequences', 'Drip campaigns', 'Lead scoring', 'Conversion tracking'] },
    { title: 'Analytics & Reporting', desc: 'Measure and improve performance', items: ['Real-time dashboards', 'Conversion tracking', 'Attribution modeling', 'Monthly reports'] }
  ];

  const platforms = [
    { name: 'Google Ads', items: ['Search campaigns', 'Display network', 'YouTube ads', 'Shopping campaigns'] },
    { name: 'Facebook & Instagram', items: ['Feed ads', 'Stories ads', 'Carousel ads', 'Video ads'] },
    { name: 'LinkedIn Ads', items: ['Sponsored content', 'Message ads', 'Dynamic ads', 'Lead gen forms'] },
    { name: 'TikTok Ads', items: ['In-feed ads', 'Brand takeovers', 'Hashtag challenges', 'Spark ads'] }
  ];

  const results = [
    { metric: '450%', label: 'Average ROI Increase' },
    { metric: '65%', label: 'Cost Per Lead Reduction' },
    { metric: '3.2x', label: 'Lead Quality Improvement' },
    { metric: '85%', label: 'Conversion Rate Increase' }
  ];

  const process = [
    { num: '01', title: 'Audience Research', desc: 'Deep dive into your ideal customer profile and market analysis' },
    { num: '02', title: 'Campaign Strategy', desc: 'Develop targeted campaigns across multiple platforms and channels' },
    { num: '03', title: 'Creative Development', desc: 'Design compelling ad creatives that resonate with your audience' },
    { num: '04', title: 'Launch & Optimize', desc: 'Monitor performance and continuously optimize for better results' }
  ];

  return (
    <div className="min-h-screen page-transition" style={{ background: 'var(--bg-primary)', paddingTop: '80px', position: 'relative' }}>
      <BackgroundAnimation />
      
      <section className="py-24" style={{ padding: '120px 7.6923% 80px' }}>
        <div className="max-w-5xl mx-auto text-center">
          <h1 className="display-huge mb-6" style={{ color: 'var(--text-primary)' }}>
            Lead Generation &
            <span className="block" style={{ color: 'var(--brand-primary)' }}>Ad Campaigns</span>
          </h1>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>Generate More Qualified Leads</p>
          <p className="body-medium mb-8" style={{ color: 'var(--text-secondary)', maxWidth: '800px', margin: '0 auto 32px' }}>
            Stop wasting money on unqualified leads. Our precision-targeted ad campaigns deliver high-quality prospects that actually convert into paying customers.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://calendly.com/americanprimeforyou" target="_blank" rel="noopener noreferrer" className="button-text dark-button-animate inline-flex items-center space-x-2" style={{ background: 'var(--brand-primary)', color: '#000000', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>
              <span>Get More Leads</span>
              <ArrowRight size={20} />
            </a>
            <a href="/services" className="button-text dark-button-animate" style={{ background: 'rgba(255, 255, 255, 0.1)', color: 'var(--text-primary)', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>View All Services</a>
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>Lead Generation Strategies</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {strategies.map((strategy, idx) => (
              <div key={idx} className="p-6 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border-subtle)' }}>
                <h3 className="heading-2 mb-2" style={{ color: 'var(--brand-primary)' }}>{strategy.title}</h3>
                <p className="body-small mb-4" style={{ color: 'var(--text-secondary)' }}>{strategy.desc}</p>
                <ul className="space-y-1">
                  {strategy.items.map((item, i) => (
                    <li key={i} className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--brand-primary)' }}>• </span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%', background: 'rgba(0, 255, 209, 0.02)', borderTop: '1px solid rgba(0, 255, 209, 0.1)', borderBottom: '1px solid rgba(0, 255, 209, 0.1)' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>Multi-Platform Advertising</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {platforms.map((platform, idx) => (
              <div key={idx} className="p-6 rounded-lg" style={{ background: 'rgba(255, 255, 255, 0.03)', border: '1px solid var(--border-subtle)' }}>
                <h3 className="heading-2 mb-4" style={{ color: 'var(--text-primary)' }}>{platform.name}</h3>
                <ul className="space-y-2">
                  {platform.items.map((item, i) => (
                    <li key={i} className="body-small" style={{ color: 'var(--text-secondary)' }}>
                      <span style={{ color: 'var(--brand-primary)' }}>• </span>{item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>Proven Results</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {results.map((result, idx) => (
              <div key={idx} className="text-center p-6 rounded-lg" style={{ background: 'rgba(0, 255, 209, 0.05)', border: '1px solid var(--brand-primary)' }}>
                <div className="display-large mb-2" style={{ color: 'var(--brand-primary)' }}>{result.metric}</div>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{result.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%', background: 'rgba(0, 255, 209, 0.02)', borderTop: '1px solid rgba(0, 255, 209, 0.1)', borderBottom: '1px solid rgba(0, 255, 209, 0.1)' }}>
        <div className="max-w-7xl mx-auto">
          <h2 className="display-large text-center mb-16" style={{ color: 'var(--text-primary)' }}>Our Process</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {process.map((step, idx) => (
              <div key={idx}>
                <div className="display-large mb-4" style={{ color: 'var(--brand-primary)' }}>{step.num}</div>
                <h3 className="heading-2 mb-3" style={{ color: 'var(--text-primary)' }}>{step.title}</h3>
                <p className="body-medium" style={{ color: 'var(--text-secondary)' }}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24" style={{ padding: '100px 7.6923%' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="display-large mb-6" style={{ color: 'var(--text-primary)' }}>Ready to Generate More Leads?</h2>
          <p className="body-large mb-8" style={{ color: 'var(--text-secondary)' }}>Let's create campaigns that deliver qualified prospects</p>
          <a href="https://calendly.com/americanprimeforyou" target="_blank" rel="noopener noreferrer" className="button-text dark-button-animate inline-flex items-center space-x-2" style={{ background: 'var(--brand-primary)', color: '#000000', padding: '14px 32px', borderRadius: '0px', textDecoration: 'none' }}>
            <span>Start Generating</span>
            <ArrowRight size={20} />
          </a>
        </div>
      </section>
    </div>
  );
};

export default LeadGeneration;